import type { LegalDoc } from "../types";
import { LEGAL_COMPANY as C } from "../company";

export const veChungToi: LegalDoc = {
  slug: "ve-chung-toi",
  title: "Về Chốt Ca",
  metaTitle: "Về chúng tôi",
  metaDescription:
    "Chốt Ca là nền tảng kết nối tuyển dụng ngành F&B tại Việt Nam. Người tìm việc dùng miễn phí, quán đăng tin và nhận đơn ứng tuyển miễn phí.",
  version: "3.0.0",
  effectiveDate: C.effectiveDate,
  intro:
    "Chốt Ca sinh ra từ một quan sát rất đời thường: ở Sài Gòn, mỗi tối cuối tuần có hàng nghìn ca làm bị bỏ trống, trong khi hàng nghìn bạn sinh viên muốn nhận việc nhưng không biết quán nào đang thiếu người. Người tìm việc phải lướt các nhóm Facebook lộn xộn rồi gọi điện hỏi từng nơi. Chủ quán thì đăng tin khắp nơi rồi sàng lọc cuộc gọi tới khuya. Chúng tôi xây Chốt Ca để rút ngắn khoảng cách đó.\n\nNgười tìm việc dùng Chốt Ca **miễn phí hoàn toàn** và sẽ giữ nguyên như vậy. Quán đăng tin và nhận đơn ứng tuyển cũng miễn phí.",
  sections: [
    {
      title: "1. Sứ mệnh",
      body: "Làm cho việc tìm và tuyển người trong ngành F&B ở Việt Nam trở thành trải nghiệm minh bạch, nhanh và an toàn — bằng công nghệ đúng vai trò của nó: hạ tầng kết nối, không phải bên thứ ba khai thác sức lao động.\n\nQuán không phải đăng tuyển rải rác trên năm nhóm Facebook rồi tự lọc bình luận. Người tìm việc không phải trả một đồng phí môi giới nào.",
    },
    {
      title: "2. Chúng tôi làm gì và không làm gì",
      items: [
        "**Có làm**: nơi để chủ quán đăng tin, nơi để người tìm việc xem tin, ứng tuyển và nhận lời mời phỏng vấn với khung giờ cụ thể.",
        "**Không làm**: không phải người sử dụng lao động, không đứng tên trong hợp đồng lao động, không tổ chức phỏng vấn thay quán.",
        "**Không giữ tiền**: không giữ lương, không thu hộ, không chi hộ, không ký quỹ. Lương là quan hệ trực tiếp giữa quán và người lao động.",
        "**Không thu tiền của người lao động**: toàn bộ tính năng dành cho người tìm việc là miễn phí, và sẽ giữ nguyên như vậy.",
      ],
    },
    {
      title: "3. Giá trị cốt lõi",
      items: [
        "**Minh bạch.** Khoảng lương ghi rõ trên mỗi tin tuyển. Không phí ẩn, không phí môi giới, không giữ lương. Tài liệu pháp lý của chúng tôi mô tả đúng những gì hệ thống đang làm, kể cả những phần chưa đẹp, thay vì viết cho hay.",
        "**Người lao động không phải trả tiền.** Doanh thu đến từ nhà tuyển dụng, không đến từ người đi tìm việc.",
        "**Tôn trọng dữ liệu.** Thu thập tối thiểu, nói rõ thông tin liên hệ của bạn được mở cho ai và vào lúc nào, và trao cho bạn công tắc để tắt. Xoá tài khoản là xoá hết.",
        "**Đúng người, đúng việc.** Gợi ý việc theo vị trí, kỹ năng và khoảng cách, kèm quy tắc chống làm phiền để không ai bị mời tràn lan.",
        "**Đứng về phía bên yếu thế hơn.** Ưu tiên cảnh báo lừa đảo và công cụ tự bảo vệ cho người tìm việc. Quy tắc cộng đồng rõ ràng, báo cáo vi phạm được đội ngũ xem xét từng trường hợp.",
        "**Vận hành có nguyên tắc.** Tuân thủ pháp luật Việt Nam, không che giấu, không lách luật.",
      ],
    },
    {
      title: "4. Pháp nhân vận hành",
      body: `**${C.legalName}**${C.legalNameEn ? `\nTên tiếng Anh: ${C.legalNameEn}` : ""}`,
      items: [
        ...(C.taxId ? [`Mã số doanh nghiệp: **${C.taxId}**`] : []),
        ...(C.incorporationDate
          ? [`Ngày bắt đầu hoạt động: ${C.incorporationDate}`]
          : []),
        ...(C.incorporationAuthority
          ? [`Cơ quan cấp đăng ký kinh doanh: ${C.incorporationAuthority}`]
          : []),
        ...(C.taxAuthority ? [`Cơ quan thuế quản lý: ${C.taxAuthority}`] : []),
        ...(C.businessLine
          ? [`Ngành nghề kinh doanh chính: ${C.businessLine}`]
          : []),
        `Địa chỉ trụ sở: ${C.address}`,
        `Người đại diện theo pháp luật: ${C.representative}`,
        `Email: ${C.email}`,
        ...(C.hotline ? [`Hotline: ${C.hotline}`] : []),
        `Website: ${C.websiteUrl}`,
        `Trang dành cho nhà tuyển dụng: ${C.employerUrl}`,
      ],
    },
    {
      title: "5. Kênh liên hệ",
      body: "Chúng tôi luôn sẵn sàng lắng nghe phản hồi từ quán tuyển dụng và người tìm việc. Chốt Ca **không có tính năng nhắn tin trong ứng dụng**, mọi liên hệ đi qua các kênh sau:",
      items: [
        `**Hỗ trợ chung**: ${C.email}`,
        `**Hợp tác và truyền thông**: ${C.legalEmail}`,
        `**Bảo vệ dữ liệu cá nhân**: ${C.dpoEmail}`,
        `**Báo cáo vi phạm và lừa đảo**: ${C.abuseEmail}`,
        `**Thanh toán và hoá đơn**: ${C.billingEmail}`,
        ...(C.hotline ? [`**Hotline**: ${C.hotline}`] : []),
        `**Zalo Official Account**: ${C.zaloOA}`,
      ],
      sections: [
        {
          title: "Giờ hỗ trợ",
          body: "Thứ Hai tới Thứ Bảy, 9:00 tới 18:00 giờ Việt Nam. Thời gian phản hồi cam kết cho từng loại yêu cầu được công bố tại [Câu hỏi thường gặp](/cau-hoi-thuong-gap) mục 5.",
        },
      ],
    },
    {
      title: "6. Văn bản pháp lý",
      body: "Toàn bộ tài liệu pháp lý của Chốt Ca công khai tại các đường dẫn sau. Bạn đọc được bất cứ lúc nào, kể cả khi chưa đăng ký tài khoản:",
      items: [
        "[Điều khoản sử dụng](/terms)",
        "[Chính sách bảo vệ dữ liệu cá nhân](/privacy)",
        "[Quy chế hoạt động của nền tảng](/platform-rules)",
        "[Thoả thuận giấy phép người dùng cuối](/eula)",
        "[Chính sách Cookie](/cookie)",
        "[Điều khoản thanh toán](/payment-terms)",
        "[Quy tắc cộng đồng](/community-guidelines)",
        "[Quy tắc dành cho Người tìm việc](/worker-conduct)",
        "[Thoả thuận dành cho Nhà tuyển dụng](/employer-agreement)",
        "[Hướng dẫn xoá dữ liệu](/data-deletion)",
        "[Câu hỏi thường gặp](/cau-hoi-thuong-gap)",
      ],
    },
  ],
  contact: {
    title: "7. Liên hệ truyền thông và hợp tác",
    prefix: "Email: ",
    email: C.email,
  },
};
